/* IC motion (sitewide). Scroll reveals built on GSAP + ScrollTrigger, tuned to match groundedpackaging.co:
   - text  : fades up 14px from a 6px blur, 0.55s (no blur under 16px text)
   - image : fades in while settling from 104% scale inside its frame, 0.9s
   - unit  : cards, steps and panels fade up 14px as one piece, 0.6s
   - fade  : marquees and carousels simply fade, 1s
   All use easeInOutQuad (GSAP "power1.inOut"). Whatever enters together staggers 75ms apart, capped at 8 steps.
   Anything already on screen (or scrolled past) when the page loads is left alone, so nothing above the fold
   flashes or waits. Skipped for reduced motion and in the theme editor; layout/theme.liquid leaves it off on
   cart, account, password, gift card and policy pages.
   Homepage: the curated HOME list below. Every other page: a general sweep of the main content (text, images,
   product / collection / article cards), skipping forms, the buy box, drawers, accordions and app widgets.
   Add data-motion="off" to any element to keep it (and everything inside it) still. */
(() => {
  if (!window.gsap || !window.ScrollTrigger) return;
  if (window.Shopify && Shopify.designMode) return;
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  // Reload and back/forward: the browser restores the previous scroll position AFTER this runs, so anything
  // claimed here as "below the fold" would then fade, blur and settle in (images from 104%) right where the
  // reader was already looking. That read as the page re-scaling on reload, so those navigations get no reveals.
  const navType = (performance.getEntriesByType('navigation')[0] || {}).type;
  if (navType === 'reload' || navType === 'back_forward') return;
  gsap.registerPlugin(ScrollTrigger);

  const EASE = 'power1.inOut', STEP = 0.075, CAP = 8, MAX = 400, MINIMG = 64;
  const DUR = { text: 0.55, unit: 0.6, image: 0.9, fade: 1 };
  const FROM = {
    text: el => ({ autoAlpha: 0, y: 14, filter: parseFloat(getComputedStyle(el).fontSize) >= 16 ? 'blur(6px)' : 'none' }),
    unit: () => ({ autoAlpha: 0, y: 14 }),
    image: () => ({ autoAlpha: 0 }),
    fade: () => ({ autoAlpha: 0 }),
  };

  // Never animated, on any page: chrome, anything interactive or third-party, and anything that opens/closes
  const SKIP = [
    'header', 'footer', '.icf', '.ic-hx', '.announcement-bar', '[class*="drawer"]', '[class*="modal"]', '[class*="popup"]',
    'form', 'product-form', 'variant-selects', 'variant-radios', '.product-form', '.product__info-container',
    '.product__info-wrapper', '[class*="quantity"]', '[class*="sticky"]', 'details', '[class*="accordion"]',
    '[class*="collapsible"]', '[class*="tab-content"]', '[class*="facets"]', '.pagination', 'predictive-search',
    '[class*="jdgm"]', '#judgeme_product_reviews', '[class*="shopify-app"]', 'iframe', 'video', '[class*="marquee-content"]',
    '.breadcrumbs', '[data-motion="off"]',
  ].join(',');

  // Homepage: explicit list, tested section by section
  const HOME = [
    ['.products-with-tabs .section-top .h2, .icc__title, .icm__title, .ictl__title, .icfc__title, .ict__title', 'text'],
    ['.icc__sub, .icm__sub, .ictl__sub, .icfc__text', 'text'],
    ['.products-with-tabs__tabs, .products-with-tabs__navigation, .icc__arrows, .icfc__actions, .ict__btn', 'unit'],
    ['.products-with-tabs__item .card, .icc__col, .icm__item, .ictl__step, .icm__card, .ict__point', 'unit'],
    ['.ictl__media, .icm__stage', 'image'],
    ['[class*="ai-marquee-container"], .ict__viewport', 'fade'],
  ];

  // Everywhere else: cards reveal as one unit, then loose text and images one by one
  const UNIT = '.grid__item, .card-wrapper, .products-with-tabs__item, .swiper-slide, .article-card, .blog-articles__article, ' +
    '.collection-list__item, .multicolumn-card, .icc__col, .icm__item, .ictl__step, .ict__card';
  const TXT = 'h1, h2, h3, h4, h5, h6, p, blockquote, .button, .btn';
  const IMG = 'img:not([class*="logo"]):not([class*="icon"])';
  const FADE = '[class*="marquee-container"], [class*="scrolling-carousel"], .ict__viewport';

  const main = document.querySelector('main') || document.body;
  const isHome = document.body.classList.contains('template-index');
  const kindOf = new Map();

  const eligible = el => {
    if (kindOf.has(el) || kindOf.size >= MAX || el.closest(SKIP)) return false;
    for (const claimed of kindOf.keys()) if (claimed !== el && claimed.contains(el)) return false; // inside a claimed unit
    const r = el.getBoundingClientRect();
    if (!r.width && !r.height) return false; // hidden (inactive tab, closed panel, collapsed)
    if (r.top < window.innerHeight && r.bottom > 0) return false; // on screen at load (even partly): leave as is
    if (r.bottom <= 0) return false; // already scrolled past (restored scroll position)
    return true;
  };
  const claim = (el, kind) => { if (eligible(el)) { kindOf.set(el, kind); gsap.set(el, FROM[kind](el)); if (kind === 'image') gsap.set(el.querySelectorAll('img'), { scale: 1.04 }); } };

  const collect = root => {
    if (isHome) { HOME.forEach(([sel, kind]) => root.querySelectorAll(sel).forEach(el => claim(el, kind))); return; }
    // 1. units (outermost first, so a card is one piece and its insides stay put)
    root.querySelectorAll(UNIT).forEach(el => { if (!el.parentElement.closest(UNIT)) claim(el, 'unit'); });
    // 2. fades (outermost strip only)
    root.querySelectorAll(FADE).forEach(el => { if (!el.parentElement.closest(FADE)) claim(el, 'fade'); });
    // 3. text leaves (skip wrappers that hold images or other text elements)
    // (never inside a card or strip: those move as one piece or not at all)
    root.querySelectorAll(TXT).forEach(el => { if (!el.closest(UNIT + ', ' + FADE) && !el.querySelector('img, iframe, video, ' + TXT)) claim(el, 'text'); });
    // 4. images: animate the frame when it clips (the zoom stays inside), otherwise the image itself
    root.querySelectorAll(IMG).forEach(img => {
      if (img.closest(UNIT + ', ' + FADE)) return;
      const w = parseInt(img.getAttribute('width') || '0', 10) || img.getBoundingClientRect().width;
      if (w && w < MINIMG) return;
      const frame = img.parentElement;
      const clips = frame && frame !== root && getComputedStyle(frame).overflow === 'hidden' && frame.querySelectorAll('img').length <= 2;
      if (clips) claim(frame, 'image'); else claim(img, 'fade');
    });
  };

  const play = (el, i) => {
    const kind = kindOf.get(el); if (!kind) return;
    const delay = Math.min(i, CAP) * STEP;
    gsap.to(el, { autoAlpha: 1, y: 0, filter: 'blur(0px)', duration: DUR[kind], ease: EASE, delay,
      onComplete: () => gsap.set(el, { clearProps: 'opacity,visibility,transform,filter' }) });
    if (kind === 'image') gsap.to(el.querySelectorAll('img'), { scale: 1, duration: DUR.image, ease: EASE, delay, clearProps: 'transform' });
  };
  const watch = els => { if (els.length) ScrollTrigger.batch(els, { start: 'top 94%', once: true, interval: 0.1, onEnter: batch => batch.forEach(play) }); };

  collect(main);
  watch([...kindOf.keys()]);

  // "Spotted out in the wild": the photos on screen appear one at a time, left to right, tied to the scroll
  // position (scrubbed, so scrolling back up reverses it). GSAP only drives one 0-1 value (--icfc-reveal) on the
  // row; the row's own script turns that into a per-slot reveal every frame, so it stays one-at-a-time even while
  // the endless loop moves or is dragged. Left alone if the row is already on screen when the page loads.
  document.querySelectorAll('.icfc__viewport').forEach(view => {
    const r = view.getBoundingClientRect();
    if (r.top < window.innerHeight && r.bottom > 0) return;
    gsap.fromTo(view, { '--icfc-reveal': 0 }, { '--icfc-reveal': 1, ease: 'none',
      scrollTrigger: { trigger: view, start: 'top 90%', end: 'top 40%', scrub: 0.6 } });
  });

  // Content added later (load more, recommendations, reviews loading in): pick up whatever lands below the fold
  if ('MutationObserver' in window) {
    let pending = false;
    new MutationObserver(muts => {
      if (pending || !muts.some(m => m.addedNodes.length)) return;
      pending = true;
      setTimeout(() => {
        pending = false;
        const before = new Set(kindOf.keys());
        collect(main);
        watch([...kindOf.keys()].filter(el => !before.has(el)));
        ScrollTrigger.refresh();
      }, 200);
    }).observe(main, { childList: true, subtree: true });
  }

  // Keep ScrollTrigger in step with Lenis smooth scrolling, and re-measure once images have loaded
  const hookLenis = () => { if (window.icLenis && !hookLenis.done) { window.icLenis.on('scroll', ScrollTrigger.update); hookLenis.done = true; } };
  hookLenis();
  document.addEventListener('DOMContentLoaded', hookLenis);
  window.addEventListener('load', () => { hookLenis(); ScrollTrigger.refresh(); });
})();
